import type { TypeTheme } from "@revolt/state/stores/Theme";

/**
 * Konkord: a few tiled stars and a faint glow, drawn by CSS (no image)
 */
const STARS = [
  "radial-gradient(1px 1px at 23px 41px, #ffffffcc, transparent)",
  "radial-gradient(1px 1px at 141px 97px, #ffffff99, transparent)",
  "radial-gradient(1.5px 1.5px at 211px 179px, #ffffffbb, transparent)",
  "radial-gradient(1px 1px at 77px 213px, #c7d0ff99, transparent)",
  "radial-gradient(1px 1px at 263px 23px, #ffffff88, transparent)",
  "radial-gradient(1.5px 1.5px at 169px 271px, #e0e4ffaa, transparent)",
  "radial-gradient(1px 1px at 311px 137px, #ffffff77, transparent)",
  "radial-gradient(ellipse 60% 45% at 85% 0%, #7c8cff1f, transparent)",
  "radial-gradient(ellipse 50% 40% at 0% 100%, #b07cff17, transparent)",
]
  .map((layer, i, all) =>
    // the glows cover the whole area, the stars repeat every 340px
    i >= all.length - 2 ? layer : `${layer} 0 0 / 340px 340px`,
  )
  .join(", ");

/**
 * Konkord: one colour of the gradient at the chosen strength
 */
function fade(colour: string, strength: number) {
  return `color-mix(in srgb, ${colour} ${strength}%, transparent)`;
}

/**
 * Konkord: background behind the whole app: gradient and stars over the
 * darkest surface, or just that surface
 * @param theme Theme options
 * @param darkMode Whether dark mode is on
 * @returns CSS background layers, or undefined for a flat background
 */
export function konkordBackdrop(
  theme: Pick<
    TypeTheme,
    | "backdrop"
    | "backdropFrom"
    | "backdropTo"
    | "backdropAngle"
    | "backdropStrength"
    | "stars"
  >,
  darkMode: boolean,
) {
  const layers: string[] = [];
  if (theme.stars && darkMode) layers.push(STARS);

  // light mode needs stronger colours to show over white
  const strength = Math.min(100, theme.backdropStrength * (darkMode ? 1 : 1.3));
  const from = fade(theme.backdropFrom, strength);
  const to = fade(theme.backdropTo, strength);

  switch (theme.backdrop) {
    case "linear":
      layers.push(`linear-gradient(${theme.backdropAngle}deg, ${from}, ${to})`);
      break;
    case "glow":
      layers.push(
        `radial-gradient(ellipse 70% 60% at 0% 0%, ${from}, transparent 70%)`,
        `radial-gradient(ellipse 70% 60% at 100% 100%, ${to}, transparent 70%)`,
      );
      break;
    case "aurora":
      layers.push(
        `radial-gradient(ellipse 60% 50% at 15% 0%, ${from}, transparent 70%)`,
        `radial-gradient(ellipse 55% 45% at 85% 10%, ${to}, transparent 70%)`,
        `radial-gradient(ellipse 80% 50% at 50% 100%, ${fade(theme.backdropTo, strength * 0.6)}, transparent 70%)`,
        `linear-gradient(${theme.backdropAngle}deg, ${fade(theme.backdropFrom, strength * 0.35)}, ${fade(theme.backdropTo, strength * 0.35)})`,
      );
      break;
  }

  return layers.length ? layers.join(", ") : undefined;
}
